/**
 * Nion Lights — Glow Belt Preset Registry
 *
 * Each preset:
 *   id:     string  — matches the `preset` HTML attribute value
 *   name:   string  — display label for UI chips
 *   family: string  — one of: Spectrum | Calm | Festive | Warm | Beat | Motion | Solid
 *   swatch: string[]— 1–6 hex colors for UI swatch dots
 *   fn:     (i, n, t) => { h, s, l, intensity }
 *             i         = LED index 0-based
 *             n         = total LED count (default 56)
 *                         NOTE: renderer guarantees n >= 1 (defaults to 56 via parseInt || 56)
 *             t         = elapsed seconds (never resets on preset switch — no jump cuts)
 *             h         = hue 0–360
 *             s         = saturation 0–100
 *             l         = lightness 0–100 (at full brightness; renderer applies l * intensity)
 *             intensity = 0..1 — scales glow radius AND lightness
 *
 * NOTE: fn returns {h, s, l, intensity} — NOT the {color: hsl(...), intensity} shape in the
 * React design handoff (components.jsx). Dimming is extracted out so the renderer can apply
 * it uniformly without per-preset string parsing in the hot RAF path.
 */

export const PRESETS_VERSION = '2.2.0';
console.log('[nion] glow-belt-presets v' + PRESETS_VERSION);

function _washShift(t, mult) {
  const x = ((t % 32.768) / 32.768) * 256;
  const att = 15, pw = 90, ramp = 127 * att / 2;
  const peak = ramp + 127 * (pw - 2 * att) + ramp;
  let F;
  if      (x < att)              F = 127 * x * x / (2 * att);
  else if (x < pw - att)         F = ramp + 127 * (x - att);
  else if (x < pw)             { const d = pw - x;        F = peak - 127 * d * d / (2 * att); }
  else if (x < 128)              F = peak;
  else if (x < 128 + att)      { const d = x - 128;       F = peak - 127 * d * d / (2 * att); }
  else if (x < 128 + pw - att) { const d = x - 128;       F = peak - ramp - 127 * (d - att); }
  else if (x < 128 + pw)       { const d = 128 + pw - x;  F = 127 * d * d / (2 * att); }
  else                           F = 0;
  return F * mult / 128;
}
function _sin8w(x) {
  return (Math.sin(((x & 0xFF) / 256) * Math.PI * 2) * 127.5 + 128) | 0;
}
function _washColor(palIdx, h) {
  if (palIdx < 128) return { h, s: 0, l: Math.round(palIdx / 127 * 100), intensity: 1 };
  const u = (palIdx - 128) / 127;
  return { h, s: Math.round(u * 100), l: Math.round(100 - u * 50), intensity: 1 };
}

export const PRESETS = [
  {
    id: 'rainbow-flow',
    name: 'Rainbow Flow',
    family: 'Spectrum',
    swatch: ['#FF2D87', '#FF6B00', '#FFE600', '#00FF88', '#00B8FF', '#9B4DFF'],
    fn: (i, n, t) => ({
      h: ((i / n) * 360 + t * 60) % 360,
      s: 100,
      l: 60,
      intensity: 1,
    }),
  },

  {
    id: 'ocean',
    name: 'Ocean',
    family: 'Calm',
    swatch: ['#0033CC', '#0077AA', '#00BBCC'],
    fn: (i, n, t) => {
      // fx:101 Pacifica — 3 sin waves at different speeds/frequencies; combined drives blue→cyan color zones
      const w1 = Math.sin((i / n) * Math.PI * 5 + t * 0.82) * 0.5 + 0.5;
      const w2 = Math.sin((i / n) * Math.PI * 7 - t * 1.08 + 1.2) * 0.5 + 0.5;
      const w3 = Math.sin((i / n) * Math.PI * 3 + t * 0.67 + 2.4) * 0.5 + 0.5;
      const c  = (w1 + w2 + w3) / 3;
      return { h: Math.round(240 - c * 65), s: Math.round(100 - c * 40), l: Math.round(12 + c * c * 50), intensity: 1 };
    },
  },

  {
    id: 'mardi-gras',
    name: 'Mardi Gras',
    family: 'Festive',
    swatch: ['#8000FF', '#00C850', '#FFD700'],
    fn: (i, n, t) => {
      // 1-LED granularity: each LED gets its own stripe color; pattern shifts with time
      const hues = [280, 120, 45];
      const idx  = ((i + Math.floor(t * 6)) % 3 + 3) % 3;
      return { h: hues[idx], s: 100, l: 55, intensity: 1 };
    },
  },

  {
    id: 'aurora',
    name: 'Aurora',
    family: 'Spectrum',
    swatch: ['#FF0066', '#AA00FF', '#0099FF', '#00FF88'],
    fn: (i, n, t) => {
      // fx:67 Colorwaves (8_Colorwaves) — slow full-spectrum hue rotation; sin²-brightness shimmer per LED
      const hue = ((i / n) * 300 + t * 12) % 360;
      const b16 = Math.sin(((i / n) + t * 0.25) * Math.PI * 2);
      const bri = Math.max(0, b16 * b16);
      return { h: Math.round(hue), s: 80, l: Math.round(8 + bri * 50), intensity: 1 };
    },
  },

  {
    id: 'ripple',
    name: 'Ripple Rainbow',
    family: 'Spectrum',
    swatch: ['#FF2D87', '#FFE600', '#00FF88', '#00B8FF'],
    fn: (i, n, t) => {
      // fx:99 Ripple Rainbow (12_Ripple Rainbow) — up to 8 simultaneous expanding rings; each random pos+hue;
      // amplitude ramps up 8% then decays; dim hue-shifting background (aux0 walks toward aux1)
      const lifetime   = 0.5;   // ~28 frames at 60fps (speed=128 → decay=(128>>4)+1=9)
      const numRipples = 8;     // WLED supports up to 1+(n>>2)=15; 8 looks balanced
      const bgHue      = (t * 8) % 360;
      let maxContrib = 0;
      let bestHue    = bgHue;
      for (let r = 0; r < numRipples; r++) {
        const offset  = r * (lifetime / numRipples);
        const seed    = Math.floor((t + offset) / lifetime) * numRipples + r;
        const phase   = ((t + offset) % lifetime) / lifetime;         // 0..1
        const rng1    = ((seed * 1103515245 + 12345) >>> 0) / 0x100000000;
        const rng2    = ((seed * 22695477   +     1) >>> 0) / 0x100000000;
        const dropPos = Math.floor(rng1 * n);
        const hue     = rng2 * 360;
        const radius  = phase * n * 0.5;                              // expands to half strip
        const amp     = phase < 0.08
          ? phase / 0.08                                              // fast attack
          : Math.max(0, 1 - (phase - 0.08) / 0.92);                  // linear decay
        if (amp < 0.01) continue;
        const ringDist = Math.abs(Math.abs(i - dropPos) - radius);   // no wrap — linear strip
        if (ringDist < 2) {
          const c = amp * (1 - ringDist / 2);
          if (c > maxContrib) { maxContrib = c; bestHue = hue; }
        }
      }
      if (maxContrib < 0.02) return { h: bgHue, s: 40, l: 4, intensity: 1 };
      return { h: bestHue, s: 100, l: 70, intensity: maxContrib };
    },
  },

  {
    id: 'scanner',
    name: 'Dual Scanner',
    family: 'Motion',
    swatch: ['#FF2D87', '#FFE600', '#00BBFF'],
    fn: (i, n, t) => {
      // fx:60 Dual Larson Scanner (13_Scanner Dual) — two bouncing eyes mirrored at each end; hue cycles over time
      const cyc = (t * 0.8) % 2;
      const pos = cyc < 1 ? cyc * (n - 1) : (2 - cyc) * (n - 1);
      const mir = (n - 1) - pos;
      const d   = Math.min(Math.abs(i - pos), Math.abs(i - mir));
      if (d > 4) return { h: 0, s: 0, l: 0, intensity: 0 };
      return { h: Math.round((t * 40) % 360), s: 100, l: 70, intensity: 1 - d / 4 };
    },
  },

  {
    id: 'sinelon',
    name: 'Sinelon',
    family: 'Motion',
    swatch: ['#FF2D87', '#FFE600', '#00FF88', '#0099FF'],
    fn: (i, n, t) => {
      // fx:94 Sinelon Rainbow (14_Sinelon Rainbow) — beatsin16(13bpm) dot; per-frame fade_out creates trail
      // Simulated by sampling past head positions with exponential age decay (≈ fade_out per frame)
      const omega = (13 / 60) * Math.PI * 2; // 13 BPM from speed=128 → speed//10=12≈13
      let maxBri = 0;
      let trailHue = 0;
      for (let k = 0; k <= 48; k++) {
        const pos  = ((Math.sin((t - k / 60) * omega) + 1) * 0.5) * (n - 1);
        const dist = Math.abs(i - pos);
        if (dist < 1.5) {
          const bri = Math.pow(0.92, k);
          if (bri > maxBri) { maxBri = bri; trailHue = ((Math.round(pos) & 7) * 45) % 360; }
        }
      }
      if (maxBri < 0.03) return { h: 0, s: 0, l: 0, intensity: 0 };
      return { h: trailHue, s: 100, l: 70, intensity: maxBri };
    },
  },

  {
    id: 'wavesins',
    name: 'Wavesins',
    family: 'Spectrum',
    swatch: ['#FF2D87', '#FFE600', '#00FF88', '#00B8FF', '#9B4DFF'],
    fn: (i, n, t) => {
      // fx:184 Wavesins (15_Wavesins): sx=128 ix=128 c1=128 c2=128 c3=16 pal=1(rainbow)
      // bri: sin8(t*6.14 + i*π) — temporal ω=6.14 rad/s; per-LED antiphase (period 2 LEDs)
      // hue: beatsin8(128, 128..255) at 2.14Hz + per-LED phase i*π → rainbow upper half 180-360° (cyan→red)
      const bri = Math.sin(t * 6.14 + i * Math.PI) * 0.5 + 0.5;
      const hue = 180 + (Math.sin(t * Math.PI * 4.274 + i * Math.PI) * 0.5 + 0.5) * 180;
      return { h: Math.round(hue % 360), s: 100, l: Math.round(bri * 65), intensity: 1 };
    },
  },

  {
    id: 'wavesins-gold',
    name: 'Wavesins Gold',
    family: 'Warm',
    swatch: ['#FFD700', '#FF9900', '#FFBB00'],
    fn: (i, n, t) => {
      // fx:184 Wavesins (17_WavesinsGold): sx=16 ix=128 c1=128 c2=128 c3=16 pal=32(warm)
      // bri: same sin8 formula as wavesins (ix=128 unchanged) — ω=6.14 rad/s antiphase shimmer
      // hue: beatsin8(16) at 0.267Hz (8× slower) + per-LED phase i*π → warm palette upper half ~30-60° gold/amber
      const bri = Math.sin(t * 6.14 + i * Math.PI) * 0.5 + 0.5;
      const hue = 30 + (Math.sin(t * Math.PI * 0.534 + i * Math.PI) * 0.5 + 0.5) * 30; // 30-60° amber→yellow
      return { h: Math.round(hue), s: 90, l: Math.round(bri * 65), intensity: 1 };
    },
  },

  {
    id: 'running-lights',
    name: 'Running Lights',
    family: 'Spectrum',
    swatch: ['#FF2D87', '#FF6B00', '#FFE600', '#00FF88', '#00B8FF', '#9B4DFF'],
    fn: (i, n, t) => {
      // fx:15 Running Lights (34_Running) — sin8 phase wave across strip; rainbow palette per position; sx=128
      const phaseInc = 256 / n;
      const step     = (t * 8) % 256;
      const level    = Math.sin(((i * phaseInc + step) / 256) * Math.PI * 2) * 0.5 + 0.5;
      return { h: Math.round((i / n) * 360), s: 100, l: Math.round(level * level * 65), intensity: 1 };
    },
  },

  {
    id: 'washing',
    name: 'Washing',
    family: 'Calm',
    swatch: ['#FFE0A0', '#FFB830'],
    fn: (i, n, t) => {
      // fx:113 Washing Machine (45_Washing Machine) — tristate_square8 step accumulator; ix=128→6 cycles; sx=128→mult≈5.33; pal=4 amber
      const shift  = _washShift(t, 2048 / 384);
      const palIdx = _sin8w((6 * 255 * i / n + shift) & 0xFF);
      return _washColor(palIdx, 38);
    },
  },

  {
    id: 'washing-gold',
    name: 'Washing Gold',
    family: 'Warm',
    swatch: ['#FFD700', '#FFCC00'],
    fn: (i, n, t) => {
      // fx:113 Washing Machine (46_Washing Gold) — ix=255→11 cycles; sx=255→mult≈7.97; pal=4 gold
      const shift  = _washShift(t, 2048 / 257);
      const palIdx = _sin8w((11 * 255 * i / n + shift) & 0xFF);
      return _washColor(palIdx, 47);
    },
  },

  {
    id: 'washing-pink',
    name: 'Washing Pink',
    family: 'Beat',
    swatch: ['#FF00FF', '#FF88DD'],
    fn: (i, n, t) => {
      // fx:113 Washing Machine (48_Washing Pink) — 11 cycles; sx=255; pal=4 magenta
      const shift  = _washShift(t, 2048 / 257);
      const palIdx = _sin8w((11 * 255 * i / n + shift) & 0xFF);
      return _washColor(palIdx, 300);
    },
  },

  {
    id: 'washing-blue',
    name: 'Washing Blue',
    family: 'Calm',
    swatch: ['#0066FF', '#66BBFF'],
    fn: (i, n, t) => {
      // fx:113 Washing Machine (49_Washing Blue) — 11 cycles; sx=255; pal=4 blue
      const shift  = _washShift(t, 2048 / 257);
      const palIdx = _sin8w((11 * 255 * i / n + shift) & 0xFF);
      return _washColor(palIdx, 225);
    },
  },

  {
    id: 'spots-gold',
    name: 'Spots Gold',
    family: 'Warm',
    swatch: ['#FFD700', '#FF8C00'],
    fn: (i, n, t) => {
      // fx:86 Spots Fade (36_Spots Fade Gold) — 8 triangle-wave zones; threshold animates via sin; col=[#FFA000]
      const zones     = 8;
      const zoneLen   = n / zones;
      const threshold = (Math.sin(t * 1.5) * 0.5 + 0.5) * 0.75;
      const zonePos   = (i % zoneLen) / zoneLen;
      const wave      = 1 - Math.abs(zonePos * 2 - 1);
      const denom     = Math.max(0.001, 1 - threshold);
      const intensity = Math.max(0, (wave - threshold) / denom);
      return { h: 38, s: 90, l: 65, intensity };
    },
  },

  {
    id: 'spots-pink',
    name: 'Spots Pink',
    family: 'Beat',
    swatch: ['#FF00FF', '#FF66CC'],
    fn: (i, n, t) => {
      // fx:86 Spots Fade (39_Spots Fade Pink) — 8 triangle-wave zones; threshold animates via sin; col=[#FF00FF]
      const zones     = 8;
      const zoneLen   = n / zones;
      const threshold = (Math.sin(t * 1.5) * 0.5 + 0.5) * 0.75;
      const zonePos   = (i % zoneLen) / zoneLen;
      const wave      = 1 - Math.abs(zonePos * 2 - 1);
      const denom     = Math.max(0.001, 1 - threshold);
      const intensity = Math.max(0, (wave - threshold) / denom);
      return { h: 300, s: 100, l: 65, intensity };
    },
  },

  {
    id: 'multi-comet',
    name: 'Multi Comet',
    family: 'Motion',
    swatch: ['#FF2200', '#88FF00', '#00FFFF', '#AA00FF'],
    fn: (i, n, t) => {
      // fx:59 Multi Comet (24_Multi Comet) — 4 comets at staggered speeds; rainbow hues; trail fades behind head; sx=255 ix=128
      // speeds in led/sec derived from WLED formula: cycle_time = (256-sx)*(k+1)ms → (256-255)*(k+1)=1*(k+1)ms is too fast
      // using sx=128 formula: (256-128)*(k+1)=128*(k+1)ms per LED-step → 1000/128(k+1) led/sec
      const speeds = [7.8, 3.9, 2.6, 1.95];
      const hues   = [0, 90, 180, 270];
      let maxI = 0;
      let bestH = 0;
      for (let k = 0; k < 4; k++) {
        const pos  = (t * speeds[k]) % n;
        const dist = ((pos - i) % n + n) % n; // distance behind the comet head
        if (dist < 12) {
          const fade = Math.pow(0.82, dist);
          if (fade > maxI) { maxI = fade; bestH = hues[k]; }
        }
      }
      return { h: bestH, s: 100, l: 70, intensity: maxI };
    },
  },
];

/**
 * O(1) preset lookup by ID string.
 * Used by <glow-belt> attributeChangedCallback and setPreset(string).
 */
export const PRESET_MAP = new Map(PRESETS.map(p => [p.id, p]));
